/**
 * O card "Preparação do pacote" (E168.F7, GUS-180, ADR-0116 §9), na pré-análise e na mesa
 * eletrônicas.
 *
 * Depois do envio do cliente, o sistema prepara sozinho o exame (a prévia como proposta) e, em
 * PARTE, o relatório de risco, sobre a mesma versão da análise. O card mostra quatro linhas —
 * Documentos e fontes, Análise, Exame, Relatório de risco — com o motivo real de cada espera, diz
 * quando o pacote está completo e oferece "Retomar" só na saída que falhou por motivo técnico. A
 * área se relê sozinha enquanto a preparação roda. Nada aqui assina nem libera ao cliente.
 *
 * E169.F2 (GUS-155, ADR-0117 §4): assinado o laudo, o card diz se a entrega ao cliente está
 * completa (o laudo e o risco da mesma versão) ou incompleta, e a falha da liberação do risco
 * retoma sem assinar de novo.
 *
 * E170.F2 (GUS-229): o pacote só se diz completo com as saídas aprovadas pela conferência por
 * regras; a bloqueada é nomeada no alto. Quando a versão da análise ou o ponto da jornada muda
 * enquanto o card relê sozinho, ele avisa a página (`onMudou`), que relê o caso: o próximo passo,
 * a formalização e o resto da tela concordam com o card sem recarregar o navegador.
 */
import type { AxiosInstance } from 'axios';
import { useEffect, useRef } from 'react';

import { Blueprint, FalhaComSaida } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  LinhaDoPacoteData,
  PacoteDoCasoData,
  SaidaDoPacote,
} from '@/lib/preparacao_do_pacote';
import type { TelaDoPasso } from '@/lib/proximoPasso';
import {
  usePreparacaoDoPacote,
  type DesfechoDaRetomada,
  type PreparacaoDoPacote as LeituraEAcoesDoPacote,
} from '@/lib/usePreparacaoDoPacote';

import { EtapasDoPacote } from './EtapasDoPacote';
import { LinhaDoPacote } from './LinhaDoPacote';

const t = ptBR.preparacaoDoPacote;

const MOTIVOS_DA_LIBERACAO: ReadonlySet<string> = new Set([
  'liberacao_pendente',
  'reservado_nao_conferido',
  'instituicao_nao_cadastrada',
]);

function textoDaAnalise(linha: LinhaDoPacoteData): string {
  const falha = linha.falha ?? { etapa: 'processamento', causa: 'inesperada' };
  return t.avisoDaAnalise
    .replace('{etapa}', t.etapaDaFalha[falha.etapa])
    .replace('{causa}', t.causaDaFalha[falha.causa]);
}

function textoDaFalha(linha: LinhaDoPacoteData): string {
  if (linha.motivo === 'processamento_falhou') return textoDaAnalise(linha);
  if (linha.motivo != null && MOTIVOS_DA_LIBERACAO.has(linha.motivo)) return t.avisoDaLiberacao;
  return t.avisoDaFalha.replace('{saida}', t.linha[linha.chave]);
}

/**
 * A linha que pede ação no pacote. Desde 03/11 (owner; emenda do ADR-0117) o relatório de risco não
 * trava a conferência, a assinatura nem a entrega: a linha dele segue no card, mas não pede correção.
 */
function pedeAcao(linha: PacoteDoCasoData['linhas'][number]): boolean {
  return linha.chave !== 'riscos';
}

function bloqueadas(pacote: PacoteDoCasoData): string[] {
  return pacote.linhas
    .filter((l) => pedeAcao(l) && l.estado === 'bloqueado')
    .map((l) => t.linha[l.chave]);
}

function semCertificado(pacote: PacoteDoCasoData): boolean {
  return pacote.linhas.some((l) => l.motivo === 'assinatura_sem_certificado');
}

/** A frase do alto: entregue, retido sem certificado, entrega incompleta, bloqueado ou completo. */
function textoDaCompletude(pacote: PacoteDoCasoData): string {
  if (pacote.entregue) return pacote.modo === 'juizo' ? t.entregueJuizo : t.entregue;
  if (semCertificado(pacote)) return t.semCertificado;
  if (pacote.assinado) return t.entregaIncompleta;
  const nomes = bloqueadas(pacote);
  // E180.F3 (GUS-254 item 2): sem banco, a prévia reprovada pelo papel da instituição é para ler.
  if (nomes.length > 0 && pacote.banco_pendente === true) return t.bancoPendente;
  if (nomes.length > 0) {
    return (nomes.length === 1 ? t.bloqueado.um : t.bloqueado.varios).replace(
      '{saidas}',
      nomes.join(' e '),
    );
  }
  return pacote.completo ? t.completo : t.incompleto;
}

function corDaCompletude(pacote: PacoteDoCasoData): string {
  if (pacote.entregue || (pacote.completo && !pacote.assinado)) return 'text-success-700';
  if (bloqueadas(pacote).length > 0 || semCertificado(pacote)) return 'text-danger-700';
  return 'text-fg-muted';
}

function Completude({ pacote }: { pacote: PacoteDoCasoData }): JSX.Element {
  return (
    <p
      className={`text-body-sm ${corDaCompletude(pacote)}`}
      data-testid="pacote-completo"
      data-completo={pacote.completo}
      data-assinado={pacote.assinado}
      data-entregue={pacote.entregue}
    >
      {textoDaCompletude(pacote)}
    </p>
  );
}

/** O que a página mostra a partir do pacote: a versão da análise e o ponto de cada linha. */
function marcaDoPacote(pacote: PacoteDoCasoData): string {
  const linhas = pacote.linhas.map(
    (l) => `${l.chave}:${l.estado}:${l.jornada ?? ''}:${l.artefato_id ?? ''}`,
  );
  return [pacote.avaliacao_id ?? '', pacote.assinado, pacote.entregue, ...linhas].join('|');
}

/**
 * E170.F2: o pacote mudou depois da primeira leitura deste caso (a versão da análise, uma prévia
 * guardada, uma conferência): a página relê o caso. A primeira leitura e a troca de caso não avisam.
 */
function useAvisoDeMudanca(
  caseId: string,
  pacote: PacoteDoCasoData | null,
  onMudou: (() => void) | undefined,
): void {
  const anterior = useRef<{ caso: string; marca: string } | null>(null);
  const marca = pacote === null ? null : marcaDoPacote(pacote);
  useEffect(() => {
    if (marca === null) return;
    const visto = anterior.current;
    anterior.current = { caso: caseId, marca };
    if (visto !== null && visto.caso === caseId && visto.marca !== marca) onMudou?.();
  }, [caseId, marca, onMudou]);
}

function Situacao({ pacote }: { pacote: PacoteDoCasoData }): JSX.Element {
  const falhas = pacote.linhas.filter((l) => l.estado === 'falhou');
  return (
    <div className="space-y-1" data-testid="pacote-situacao">
      {pacote.versao !== null ? (
        <p className="text-caption text-fg-dim">
          {t.versao.replace('{versao}', String(pacote.versao))}
          {pacote.modo !== null ? ` · ${t.modo[pacote.modo]}` : ''}
        </p>
      ) : null}
      {pacote.preparando ? (
        <p
          role="status"
          className="text-body-sm text-accent-700"
          data-testid="pacote-preparando"
          data-etapa={pacote.etapa ?? undefined}
        >
          {pacote.etapa != null
            ? t.preparandoEtapa.replace('{etapa}', t.etapaDaPreparacao[pacote.etapa])
            : t.preparando}
        </p>
      ) : null}
      <Completude pacote={pacote} />
      {falhas.map((linha) => (
        <p
          key={linha.chave}
          role="alert"
          className="text-body-sm text-danger-700"
          data-testid={`pacote-aviso-${linha.chave}`}
        >
          {textoDaFalha(linha)}
        </p>
      ))}
    </div>
  );
}

export interface PreparacaoDoPacoteProps {
  client: AxiosInstance;
  caseId: string;
  /** A tela que mostra o card: o passo que se faz nela mesma não vira link. */
  tela: TelaDoPasso;
  /** Muda quando a página relê o caso: o pacote é lido de novo. */
  releitura?: unknown;
  /** E170.F2: o pacote mudou depois da primeira leitura; a página relê o caso. */
  onMudou?: (() => void) | undefined;
}

/**
 * E203.F5.T05 (ADR-0146): o pacote que não pede nada — completo, sem preparação em curso, sem saída
 * que falhou ou bloqueou. Na mesa ele vira a linha "Pacote completo · versão N" ao lado de "Baixar
 * exame"; enquanto prepara, falha ou bloqueia, o card fica aberto.
 */
export function pacoteSemPendencia(pacote: PacoteDoCasoData): boolean {
  return (
    pacote.completo &&
    !pacote.preparando &&
    pacote.linhas.every((l) => !pedeAcao(l) || (l.estado !== 'falhou' && l.estado !== 'bloqueado'))
  );
}

/** O card com a leitura de quem o mostra (E203.F5.T07: a mesa lê o pacote uma vez só). */
export function PreparacaoDoPacoteLido({
  client,
  caseId,
  tela,
  onMudou,
  preparacao,
}: Omit<PreparacaoDoPacoteProps, 'releitura'> & {
  preparacao: LeituraEAcoesDoPacote;
}): JSX.Element {
  const { leitura, reler, atualizar, retomar } = preparacao;
  useAvisoDeMudanca(caseId, leitura.estado === 'pronto' ? leitura.dados : null, onMudou);
  const pedirRetomada = (
    saida: SaidaDoPacote,
    justificativa: string,
  ): Promise<DesfechoDaRetomada> => retomar({ saida, justificativa });

  return (
    <Blueprint
      as="section"
      className="mt-sc-6 space-y-sc-3 p-sc-6"
      data-testid="preparacao-do-pacote"
      data-estado={leitura.estado}
      aria-labelledby="preparacao-do-pacote-titulo"
    >
      <div>
        <h3 id="preparacao-do-pacote-titulo" className="text-body-lg text-fg">
          {t.titulo}
        </h3>
      </div>
      {leitura.estado === 'carregando' ? (
        <p className="text-body-sm text-fg-muted">{t.carregando}</p>
      ) : null}
      {leitura.estado === 'erro' ? (
        // E200.F3 (GUS-220, ADR-0143 §4): a falha com saída — tentar de novo ou voltar aos casos.
        <FalhaComSaida
          mensagem={
            leitura.motivo === 'sem_permissao'
              ? t.semPermissao
              : leitura.motivo === 'nao_encontrado'
                ? t.naoEncontrado
                : t.erro
          }
          onTentarDeNovo={leitura.motivo === 'indisponivel' ? reler : undefined}
          voltar={{ para: '/casos', rotulo: ptBR.falhaComSaida.voltarAosCasos }}
          data-testid="preparacao-do-pacote-erro"
        />
      ) : null}
      {leitura.estado === 'pronto' ? (
        <>
          <EtapasDoPacote pacote={leitura.dados} />
          <Situacao pacote={leitura.dados} />
          <ul className="divide-y divide-border">
            {leitura.dados.linhas.map((linha) => (
              <LinhaDoPacote
                key={linha.chave}
                client={client}
                caseId={caseId}
                tela={tela}
                linha={linha}
                onRetomar={pedirRetomada}
                onAtualizar={atualizar}
              />
            ))}
          </ul>
        </>
      ) : null}
    </Blueprint>
  );
}

export function PreparacaoDoPacote({
  client,
  caseId,
  tela,
  releitura,
  onMudou,
}: PreparacaoDoPacoteProps): JSX.Element {
  const preparacao = usePreparacaoDoPacote(client, caseId, releitura);
  return (
    <PreparacaoDoPacoteLido
      client={client}
      caseId={caseId}
      tela={tela}
      onMudou={onMudou}
      preparacao={preparacao}
    />
  );
}
